import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAsync } from '@/lib/useAsync';
import { ErrorState, Spinner } from '@/components/ui/States';
import { toast } from '@/components/ui/Toast';
import { api, ApiError } from '@/lib/api';
import { timeAgo } from '@/lib/timeAgo';

const pillTone = (state: string) =>
  state === 'SUCCEEDED' ? 'done' : state === 'FAILED' || state === 'CANCELLED' ? 'fail' : 'run';

/* One department: its function agents, who they serve, and their recent work. */
export function PortalDepartmentPage() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const dept = useAsync(() => api.getPortalDepartment(companySlug!), [companySlug]);
  const [busyAgent, setBusyAgent] = useState<string | null>(null);

  if (dept.status === 'loading') return <Spinner label="Loading department…" />;
  if (dept.status === 'error') return <ErrorState error={dept.error} onRetry={dept.reload} />;
  const d = dept.data;

  async function runNow(agent: { id: string; name: string; projectId: string }) {
    setBusyAgent(agent.id);
    try {
      await api.startRun(agent.projectId, { agentId: agent.id });
      toast.success('Run queued', `${agent.name} is on it. The result lands under recent work.`);
      dept.reload();
    } catch (err) {
      const msg =
        err instanceof ApiError && err.code === 'no_claude'
          ? 'This install has no AI credential yet. Ask your WarriorLab contact to connect one.'
          : err instanceof ApiError
            ? err.message
            : undefined;
      toast.danger('Could not start the run', msg);
    } finally {
      setBusyAgent(null);
    }
  }

  return (
    <>
      <p style={{ fontSize: 11.5, color: 'var(--p-ink3)', marginBottom: 10 }}>
        <Link to="/portal" style={{ color: 'var(--p-ink2)', fontWeight: 600 }}>
          The Hive
        </Link>{' '}
        / {d.name}
      </p>
      <div className="portal-head">
        <h1>{d.name}</h1>
        <p>
          {d.agents.length} function agent{d.agents.length === 1 ? '' : 's'}, shared by the whole team. People drive
          the agents; the agents do the work.
        </p>
      </div>

      <div className="portal-cols">
        <div>
          {d.agents.map((a) => (
            <div key={a.id} className="portal-panel" style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <h4 style={{ marginBottom: 4 }}>{a.name}</h4>
                  <p className="body">{a.description ?? 'Specialist function agent.'}</p>
                </div>
                <button className="portal-btn" disabled={busyAgent === a.id} onClick={() => void runNow(a)}>
                  {busyAgent === a.id ? 'Queuing…' : 'Run now'}
                </button>
              </div>
            </div>
          ))}
          {d.agents.length === 0 && (
            <div className="portal-panel">
              <p className="body">No agents wired up yet. Your WarriorLab team sets these up per function.</p>
            </div>
          )}
        </div>
        <div>
          <div className="portal-panel">
            <h4>Recent work</h4>
            {d.recentRuns.length === 0 && <p className="body">Nothing yet. Run an agent to see work land here.</p>}
            {d.recentRuns.map((r) => (
              <div key={r.id} className="portal-row">
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.agentName ?? r.title}
                </span>
                <span className="meta">{timeAgo(r.at)}</span>
                <span className={`portal-pill ${pillTone(r.state)}`}>{r.state.toLowerCase()}</span>
              </div>
            ))}
          </div>
          <div className="portal-panel" style={{ marginTop: 14 }}>
            <h4>Who uses this department</h4>
            {d.people.length === 0 && <p className="body">Team roster arrives during onboarding.</p>}
            {d.people.map((p) => (
              <div key={p.id} className="portal-row">
                <span>{p.name}</span>
                <span className="meta">{p.title ?? ''}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
